import { InspectionReviewerTypeEnum } from '@/common/constants';
import { BackIcon } from '@/common/icons/BackIcon';
import SelectControl from '@/components/controls/select-control';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import useStore from '@/lib/store';
import { FC, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils.ts';

export const HeaderStewardship: FC = () => {
  const form = useForm();
  const { employee_id, district_id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { t } = useTranslation();
  const { plants, organizations, fetchOrganizations } = useStore();
  const plantId = useWatch({ control: form.control, name: 'plant_id' });

  const organizationId = useWatch({
    control: form.control,
    name: 'organization_id',
  });

  const farmerType = useWatch({
    control: form.control,
    name: 'farmer_type',
  });

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const options = useMemo(() => {
    return plants?.map((item) => ({
      label: item.name,
      value: item.id,
    }));
  }, [plants]);

  const plant = searchParams.get('plant_id');
  const organization = searchParams.get('organization_id');
  const employeeTypeParam = searchParams.get('farmer_type');

  const optionsEmployeeType = useMemo(
    () => [
      { label: 'Hammasi', value: 'all' },
      { label: 'Ustozlar', value: InspectionReviewerTypeEnum.MASTER },
      { label: 'Shogirdlar', value: InspectionReviewerTypeEnum.FARMER },
    ],
    [],
  );

  const optionsOrganization = useMemo(() => {
    const orgs =
      organizations?.map((item) => ({
        label: item.name,
        value: item.id,
      })) || [];
    return [{ label: 'Hammasi', value: 'all' }, ...orgs];
  }, [organizations]);

  useEffect(() => {
    if (!plant) {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set('plant_id', '2');
          return newParams;
        },
        { replace: true },
      );
    }
  }, []);

  useEffect(() => {
    if (plant) {
      form.setValue('plant_id', plant);
      form.setValue('organization_id', organization || 'all');
      form.setValue('farmer_type', employeeTypeParam || 'all');
    }
  }, [plant, organizationId, employeeTypeParam]);

  useEffect(() => {
    if (plantId) {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set('plant_id', plantId);
          return newParams;
        },
        { replace: true },
      );
    }
  }, [plantId]);

  const canShow = false;

  return (
    <Form {...form}>
      <div className="py-2.5 px-10 flex justify-between items-center border-b  border-gray-smoke">
        <div className="flex items-center gap-5">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="!py-0 px-2 h-[32px]"
          >
            <BackIcon />
          </Button>
          <p className="text-lg leading-7 text-midnight-default font-medium">
            {t('stewardship')}
          </p>
        </div>
        <div className="flex gap-4">
          <SelectControl
            label={t('plant.type')}
            className="w-[200px] flex gap-2 items-center"
            control={form?.control}
            labelClass="text-nowrap"
            name="plant_id"
            options={options}
            notRequired
          />
          {!employee_id && (
            <SelectControl
              label={t('organization')}
              className={cn('w-auto flex gap-2 items-center')}
              control={form?.control}
              labelClass="text-nowrap"
              name="organization_id"
              options={optionsOrganization}
              onChange={(value) => {
                if (value === 'all') {
                  form.setValue('organization_id', 'all');
                  setSearchParams(
                    (prev) => {
                      const newParams = new URLSearchParams(prev);
                      newParams.delete('organization_id');
                      return newParams;
                    },
                    { replace: true },
                  );
                } else {
                  setSearchParams(
                    (prev) => {
                      const newParams = new URLSearchParams(prev);
                      newParams.set('organization_id', value);
                      return newParams;
                    },
                    { replace: true },
                  );
                }
              }}
              notRequired
            />
          )}
          {canShow && (
            <SelectControl
              label={t('employee.type')}
              className={cn(
                'w-auto flex gap-2 items-center',
                district_id && !employee_id
                  ? 'opacity-60 pointer-events-none'
                  : '',
              )}
              control={form?.control}
              labelClass="text-nowrap"
              name="farmer_type"
              options={optionsEmployeeType}
              onChange={(value) => {
                if (value === 'all') {
                  form.setValue('farmer_type', 'all');
                  setSearchParams(
                    (prev) => {
                      const newParams = new URLSearchParams(prev);
                      newParams.delete('farmer_type');
                      return newParams;
                    },
                    { replace: true },
                  );
                } else {
                  setSearchParams(
                    (prev) => {
                      const newParams = new URLSearchParams(prev);
                      newParams.set('farmer_type', value);
                      return newParams;
                    },
                    { replace: true },
                  );
                }
              }}
              notRequired
            />
          )}
        </div>
        <span></span>
      </div>
    </Form>
  );
};
